import React, { useCallback, useMemo, useState} from 'react'
import apiClient from '../utils/api/apiClient'
import { APIEndpoints } from '../utils/api/apiConfig'
import { emptyGuid } from '../utils/emptyGuid'
import { useWithdrawalsState } from './WithdrawalsProvider'
import { cloneDeep } from 'lodash';
import { setValueByKey } from '../utils/utils'
import { setPointerErrorsFromApiResponse } from "../utils/api/apiUtils"

const PickingPrepareDataContext = React.createContext({})
export function usePickingPrepareDataState() {
  return React.useContext(PickingPrepareDataContext)
}

const positionsTemplate = []
const headerDataTemplate = {
  id: emptyGuid,
  displayName: '',
  recipient: {
    id: emptyGuid
  },
  office: {
    id: emptyGuid
  },
  costCenter: {
    id: emptyGuid
  },
  sourceStorehouse: {
    id: emptyGuid
  },
  targetStorehouse: {
    id: emptyGuid
  }
}

const PickingPrepareDataProvider = ({ children }) => {
  const [successSubmit, setSuccessSubmit] = useState(false)
  const [headerData, setHeaderData] = useState(cloneDeep(headerDataTemplate))
  const [initialHeaderData, setInitialHeaderData] = useState('')

  const [positions, setPositions] = useState(cloneDeep(positionsTemplate))
  const [initialPositions, setInitialPositions] = useState('')
  const [formErrors, setFormErrors] = useState({})
  const [errorsOccurredOnSubmit, setErrorsOccurredOnSubmit] = useState(false)

  const [loadedOrderData, setLoadedOrderData] = useState(null)
  const {setForceReloadWithdrawals, setFilterSOHeader } = useWithdrawalsState()

  const [partialPickingResult, setPartialPickingResult] = useState({})
  const setHeaderDataByKey = useCallback((key, value) => {
    setHeaderData(prev => setValueByKey(key, value, prev))
  }, [])
  const setPositionsByKey = useCallback((key, value) => {
    setPositions(prev => setValueByKey(key, value, prev))
  }, [])

  const resetErrorByFormKey = useCallback(formKey => {
    setFormErrors(prev => {
      if (prev[formKey]) {
        delete prev[formKey]
      }
      return prev
    })
  }, [setFormErrors])

  const loadOrderData = useCallback(async (id) =>  {
    let loadedData = await apiClient.getJson(APIEndpoints.withdrawals(id).pickingPreparation)
    setLoadedOrderData(loadedData)
    initLoadedOrderData(cloneDeep(loadedData));
  }, [])

  const initLoadedOrderData = (loadedData) => {
    const loadedHeaderData = loadedData.header
    const loadedPositions = loadedData.positions || []

    // init headerData fields
    const newHeaderData = {
      ...loadedHeaderData,
      recipient: loadedHeaderData.recipient ? loadedHeaderData.recipient : { id: emptyGuid },
      office: loadedHeaderData.office ? loadedHeaderData.office : { id: emptyGuid },
      costCenter: loadedHeaderData.costCenter ? loadedHeaderData.costCenter : { id: emptyGuid },
      sourceStorehouse: loadedHeaderData.sourceStorehouse ? loadedHeaderData.sourceStorehouse : { id: emptyGuid },
      targetStorehouse: loadedHeaderData.targetStorehouse ? loadedHeaderData.targetStorehouse : { id: emptyGuid },
    };
    setHeaderData(newHeaderData)
    setInitialHeaderData(JSON.stringify(newHeaderData));

    // init positions
    const newPositions = [...loadedPositions]
    setPositions(newPositions)
    setInitialPositions(JSON.stringify(newPositions))
  }

  const headerDataHasChanged = useMemo(() => {
    if (initialHeaderData !== '') {
      const headerDataString = JSON.stringify(headerData)
      return headerDataString !== initialHeaderData
    }
  }, [headerData, initialHeaderData])

  const positionsHasChanged = useMemo(() => {
    if (initialPositions !== '') {
      const positionsString = JSON.stringify(positions)
      return positionsString !== initialPositions
    }
  }, [positions, initialPositions])



  const formDataHasChanged = useMemo(() => {
    return headerDataHasChanged || positionsHasChanged
  }, [headerDataHasChanged, positionsHasChanged])



  const resetFormData = useCallback(() => {
    setHeaderData(JSON.parse(initialHeaderData))
    setPositions(JSON.parse(initialPositions))
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
  }, [setFormErrors, setErrorsOccurredOnSubmit, initialHeaderData, initialPositions])

  const getPreparedPositions = useCallback(() => {
    const preparedPositions = cloneDeep(positions)
    preparedPositions.forEach(item => {
      delete item.validation
      delete item.metaProperties
    })
    return preparedPositions
  }, [positions])

  const partialPickingIsEnabled = useMemo(() => {
    return getPreparedPositions().some((pos) => !!pos.quantityCounted)
  }, [getPreparedPositions])

  const submitPreparation = async () => {
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)

    const updatedData = {
      positions :  getPreparedPositions().map((pos) => {
        let preparedPos = { Id: pos.id, QuantityCounted: pos.quantityCounted };
        return preparedPos;
      })
    }
    try {
      let result = await apiClient.postJson(APIEndpoints.withdrawals(headerData.id).pickingPreparation, updatedData)
      setPartialPickingResult(result)
      setFilterSOHeader(headerData.shippingNo)
      setSuccessSubmit(true)
      await loadOrderData(headerData.id)
      setForceReloadWithdrawals(true)
    } catch (e) {
      setErrorsOccurredOnSubmit(true)
      setFormErrors(setPointerErrorsFromApiResponse(e))
    }
  }
  const contextValues = {
    loadOrderData,
    submitPreparation,
    formErrors, setFormErrors,
    resetErrorByFormKey,
    errorsOccurredOnSubmit,
    formDataHasChanged,
    resetFormData,
    setHeaderDataByKey,
    headerData,
    setPositionsByKey,
    setPositions,
    positions,
    setSuccessSubmit,
    successSubmit,
    loadedOrderData,
    partialPickingResult, partialPickingIsEnabled
  }
  return (
    <PickingPrepareDataContext.Provider value={contextValues}>
      {children}
    </PickingPrepareDataContext.Provider>
  )
}

export default PickingPrepareDataProvider
